import type { Modality } from "../types";

/** Shown whenever a compare set mixes pure image + pure video models. */
export const MIXED_MODALITY_COMPARE_MSG =
  "Can't compare image and video models together. Pick all image or all video.";

export type ModalityLike = Modality | string;

/**
 * Same-class rule:
 * - pure `image` + `both` → OK (image class)
 * - pure `video` + `both` → OK (video class)
 * - only `both` → OK
 * - any pure `image` AND any pure `video` → reject
 */
export function isMixedModalityCompare(
  models: ReadonlyArray<{ modality: ModalityLike }>,
): boolean {
  let hasImage = false;
  let hasVideo = false;
  for (const m of models) {
    if (m.modality === "image") hasImage = true;
    else if (m.modality === "video") hasVideo = true;
    if (hasImage && hasVideo) return true;
  }
  return false;
}

/** True if adding `candidate` to `existing` would create a mixed image+video set. */
export function wouldMixModality(
  existing: ReadonlyArray<{ modality: ModalityLike }>,
  candidate: { modality: ModalityLike },
): boolean {
  return isMixedModalityCompare([...existing, candidate]);
}

/**
 * Resolve model ids → models and return whether the set is mixed.
 * Unknown ids are skipped (they don't contribute a class).
 */
export function compareIdsAreMixed(
  ids: readonly string[],
  models: ReadonlyArray<{ id: string; modality: ModalityLike }>,
): boolean {
  const resolved = ids
    .map((id) => models.find((m) => m.id === id))
    .filter((m): m is { id: string; modality: ModalityLike } => Boolean(m));
  return isMixedModalityCompare(resolved);
}
